import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { produce } from 'immer';
import { useMemo } from 'react';
import { useShowable } from 'utils/hooks/useShowable';
import type { EditSettingPayload } from './EditSettingDialog';
import type { CustomPortSetting, CustomPortSettingIndexed } from './model';
import type {
  AddSettingCommand,
  CustomPortSettingsCommands,
  CustomPortSettingsModel,
  CustomPortSettingsShowables,
  DeleteSettingsCommand,
  EditSettingCommand,
} from './useCustomPortSettings';

interface Props {
  customPortSettings?: CustomPortSetting[];
  onCustomPortSettingsChange?: (value: CustomPortSetting[]) => void;
}

export const useCustomPortSettingsModel = (
  props: Props,
): CustomPortSettingsModel => {
  const customPortSettings = props.customPortSettings ?? [];
  const customPortSettingsIndexed: CustomPortSettingIndexed[] = useMemo(
    () => customPortSettings.map((setting, index) => ({ ...setting, index })),
    [customPortSettings],
  );

  const showables: CustomPortSettingsShowables = {
    addSetting: useShowable(),
    editSetting: useShowable<EditSettingPayload>(),
  };

  const confirmDialog = useSimpleConfirmDialog();

  const addSetting = useStableCallback((command: AddSettingCommand) => {
    const newSettings = [...customPortSettings, command.setting];
    props.onCustomPortSettingsChange?.(newSettings);
    showables.addSetting.hide();
  });

  const deleteSettings = useStableCallback(
    async (command: DeleteSettingsCommand) => {
      const newSettings = customPortSettings.filter(
        (_setting, index) => !command.indexes.has(index),
      );
      props.onCustomPortSettingsChange?.(newSettings);
    },
  );

  const editSetting = useStableCallback(async (command: EditSettingCommand) => {
    const newSettings = produce(customPortSettings, (draft) => {
      draft[command.index] = command.setting;
    });
    props.onCustomPortSettingsChange?.(newSettings);
    showables.editSetting.hide();
  });

  const commands: CustomPortSettingsCommands = useMemo(
    () => ({
      addSetting,
      deleteSettings,
      editSetting,
    }),
    [],
  );

  return {
    commands,
    confirmDialog,
    customPortSettingsIndexed,
    showables,
  };
};
